import type { Gate } from '@/data/permissions';
import {
  BookText,
  ChartColumn,
  ClipboardCheck,
  ClipboardList,
  Container,
  FileCheck,
  FilePen,
  Handshake,
  History,
  IdCard,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  MessagesSquare,
  NotebookTabs,
  Package,
  PackageCheck,
  ReceiptText,
  Route,
  ShieldCheck,
  Truck,
  Users,
  Wallet,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';

/**
 * The sidebar, and therefore the router. One list feeds both, so a route can never exist without a
 * way to reach it or a name to call it by.
 *
 * `id` is the translation key in BOTH namespaces: `nav.<id>` for the menu label and `pages.<id>` for
 * the screen's own title and description. No label is written here — copy lives in the locale files.
 *
 * The menu itself comes from docs/chatgpt/2026-09-19-branch-software-name-proposal.md (Phase 3), which
 * is the agency's own wording, and the ordering in docs/estilo-visual.md.
 */
export interface NavLeaf {
  id: string;
  path: string;
  icon: LucideIcon;
  /** Also the active leaf on the pages under its path that are not in the menu (`/loads/new`,
   *  `/loads/<id>`). Only for a leaf with no menu children below its path. */
  coversSubpaths?: boolean;
  /** Shown in the menu only when the caller passes this gate (a money screen for the money roles). The
   *  route itself still exists and answers anyone else with its own "not for your role" state. */
  gate?: Gate;
  /** Shown in the menu only while that feature is in use for the agency (on top of `gate`): the screen
   *  still answers its URL, with the explained off state. `driverSettlements` = some carrier's drivers are
   *  paid by the agency or an owner, or some settlement exists (fleet-driver-pay). */
  feature?: NavFeature;
}

export type NavFeature = 'driverSettlements';

export interface NavSection extends NavLeaf {
  children?: NavLeaf[];
}

export const NAVIGATION: NavSection[] = [
  { id: 'dashboard', path: '/dashboard', icon: LayoutDashboard },
  { id: 'documentation', path: '/documentation', icon: BookText },
  {
    id: 'brokers',
    path: '/brokers',
    icon: Handshake,
    children: [
      { id: 'brokersSetups', path: '/brokers/setups', icon: ClipboardCheck },
      { id: 'brokersCreditCheck', path: '/brokers/credit-check', icon: ShieldCheck },
      { id: 'brokersRateConfirmations', path: '/brokers/rate-confirmations', icon: FilePen },
      { id: 'brokersBolsPods', path: '/brokers/bols-pods', icon: FileCheck },
      // audit-e M1: the shippers and receivers the agency reuses (a stop is filled from one).
      { id: 'brokersFacilities', path: '/brokers/facilities', icon: Warehouse },
      // Broker receivables (what brokers owe the carriers); Receipts stays the load's expense receipts.
      { id: 'brokersPayments', path: '/brokers/payments', icon: Landmark },
      { id: 'brokersReceipts', path: '/brokers/receipts', icon: ReceiptText },
    ],
  },
  {
    id: 'carriers',
    path: '/carriers',
    icon: Truck,
    children: [
      { id: 'carriersPackages', path: '/carriers/packages', icon: PackageCheck, coversSubpaths: true },
      // The agency ledger. Money: admin and accounting only (invoice_read RLS, invoice_raise gate).
      { id: 'carriersInvoices', path: '/carriers/invoices', icon: ReceiptText, gate: 'invoice.read' },
      { id: 'carriersTrucks', path: '/carriers/trucks', icon: Container },
      { id: 'carriersDrivers', path: '/carriers/drivers', icon: IdCard },
      // owner-fees: the weekly statements of leased trucks. Money: admin and accounting only.
      { id: 'carriersOwnerStatements', path: '/carriers/owner-statements', icon: NotebookTabs, coversSubpaths: true, gate: 'owner_fee.read' },
      // fleet-driver-pay: weekly driver settlements. Money roles, and only while the agency or an owner pays
      // some carrier's drivers (or a settlement exists): an agency whose carriers pay their drivers sees nothing new.
      {
        id: 'carriersDriverSettlements',
        path: '/carriers/driver-settlements',
        icon: Wallet,
        coversSubpaths: true,
        gate: 'driver_pay.manage',
        feature: 'driverSettlements',
      },
    ],
  },
  { id: 'reports', path: '/reports', icon: ChartColumn },
  {
    id: 'dispatchers',
    path: '/dispatchers',
    icon: Users,
    children: [
      { id: 'dispatchersAssignedDrivers', path: '/dispatchers/assigned-drivers', icon: IdCard },
      { id: 'dispatchersDispatch', path: '/dispatchers/dispatch', icon: Route },
      // dispatcher-pay: the third ledger. Everyone in the office reaches it: admin and accounting see every
      // dispatcher's pay, anyone else only his own (the RLS decides, not the menu).
      { id: 'dispatchersPay', path: '/dispatchers/pay', icon: Wallet },
    ],
  },
  { id: 'loads', path: '/loads', icon: Package, coversSubpaths: true },
  { id: 'tasks', path: '/tasks', icon: ClipboardList },
  // activity-feed: what the team and the drivers did (the bell's Activity tab, paginated, with filters).
  { id: 'activity', path: '/activity', icon: History, gate: 'activity.read' },
  { id: 'chat', path: '/chat', icon: MessagesSquare, coversSubpaths: true },
  { id: 'support', path: '/support', icon: LifeBuoy },
];

/** Flattened, for the router. */
export const NAV_ROUTES: NavLeaf[] = NAVIGATION.flatMap((section) => [
  section,
  ...(section.children ?? []),
]);
